import React from "react";
import { Field, FieldArray, useFormikContext } from "formik";
import { get } from "lodash";
import {
  FieldButtonGroup,
  FieldDropSide,
  MultiSelectDropdown,
  CsplTable as Table,
  Link,
  FieldInput,
  SearchableList,
  DayDropdown,
  DatePicker,
  FieldInputNumber,
} from "../../../../../components";
import {
  yesNoOptions,
  toOptionValuesFromMapper,
  OPTIONS_DATA_MAPPER,
  toMultiSelectValueById,
  required,
  usDateFormat,
  getPathWithParam,
  FLOW_TYPES,
  getNullTableItem,
} from "../../../../../utils";
import payrollFrequency from "../../../../../mocks/payrollFrequency.json";
import weekdays from "../../../../../mocks/weekdays.json";
import days from "../../../../../mocks/days.json";
import months from "../../../../../mocks/months.json";

const columns = [
  {
    label: "Investment Name",
    className: "column-investment-name",
    columnName: "investmentName",
  },
  {
    label: "Allocation %",
    className: "column-allocation-perc",
    columnName: "allocationPercentage",
  },
];

const ModelPortfolio = ({
  fields,
  isEdit,
  isSave,
  investmentsList,
  isPlan = false,
  setTabGrey,
  investmentStatusOptions,
}) => {
  const { values, setFieldValue, handleChange } = useFormikContext();
  const percentageElectionsList = get(values, "percentageElections") || [];
  const totalAllocationPerc = percentageElectionsList.reduce(
    (total, current) => {
      return total + parseInt(current.allocationPercentage || "0", 10);
    },
    0
  );
  const onDaySelected = (fieldName, value) => {
    setFieldValue(fieldName, value);
  };

  return (
    <div>
      <Field
        size="md"
        isRequired
        name={fields.name}
        label={"Investment Name"}
        type="text"
        autoComplete="off"
        value={values[fields.name]}
        onChange={(e) => {
          setTabGrey(1);
          handleChange(e);
        }}
        disabled={isPlan ? true : isEdit && !isSave}
        component={FieldInput}
      />
      <Field
        name={fields.status}
        isRequired
        label="Investment Status"
        value={values[fields.status]}
        disabled={isEdit && !isSave}
        options={investmentStatusOptions}
        popupContent={
          <SearchableList
            label="Select an investment status"
            options={investmentStatusOptions}
            onSelect={(value) => setFieldValue(fields.status, value)}
            onChange={() => {
              setTabGrey(1);
              setFieldValue(fields.suspendedFromDate, null);
              setFieldValue(fields.suspendedToDate, null);
              setFieldValue(fields.inceptionDate, null);
            }}
            disabled={isEdit && !isSave}
            isNotTypeAhead
          />
        }
        component={FieldDropSide}
      />
      {values[fields.status] === 4 && (
        <>
          <Field
            size="smd"
            name={fields.suspendedFromDate}
            placeholder="To"
            value={usDateFormat(values[fields.suspendedFromDate])}
            direction="right"
            isDatePicker
            isRequired
            label="Suspended From"
            onClear={() => setFieldValue(fields.suspendedFromDate, "")}
            popupContent={
              <DatePicker
                onDayClick={(value) => {
                  setTabGrey(1);
                  setFieldValue(fields.suspendedFromDate, value);
                }}
                value={usDateFormat(values[fields.suspendedFromDate])}
              />
            }
            disabled={isEdit && !isSave}
            component={FieldDropSide}
          />
          <Field
            size="smd"
            name={fields.suspendedToDate}
            placeholder="To"
            value={usDateFormat(values[fields.suspendedToDate])}
            direction="right"
            isDatePicker
            onClear={() => setFieldValue(fields.suspendedToDate, "")}
            label="Suspended to"
            isRequired
            popupContent={
              <DatePicker
                onDayClick={(value) => {
                  setTabGrey(1);
                  setFieldValue(fields.suspendedToDate, value);
                }}
                value={usDateFormat(values[fields.suspendedToDate])}
              />
            }
            disabled={isEdit && !isSave}
            component={FieldDropSide}
          />
        </>
      )}
      <Field
        size="lg"
        isRequired
        name={fields.riskType}
        label={"Risk Type"}
        options={toOptionValuesFromMapper(
          OPTIONS_DATA_MAPPER.MODEL_INV_RISK_TYPE
        )}
        selectedValue={values[fields.riskType]}
        value={values[fields.riskType]}
        onChange={(value) => {
          setTabGrey(1);
          setFieldValue(fields.riskType, value);
        }}
        component={FieldButtonGroup}
        disabled={isPlan ? true : isEdit && !isSave}
      />
      <Field
        size="sm"
        isRequired
        name={fields.is100PercAllocated}
        label={"Is this a 100% Allocated Model?"}
        options={yesNoOptions}
        selectedValue={values[fields.is100PercAllocated]}
        value={values[fields.is100PercAllocated]}
        onChange={(value) => {
          setTabGrey(1);
          setFieldValue(fields.is100PercAllocated, value);
        }}
        component={FieldButtonGroup}
        disabled={isPlan ? true : isEdit && !isSave}
      />
      <Field
        size="sm"
        isRequired
        name={fields.isReallocationAllowed}
        label={"Reallocation Allowed?"}
        options={yesNoOptions}
        selectedValue={values[fields.isReallocationAllowed]}
        value={values[fields.isReallocationAllowed]}
        onChange={(value) => {
          setTabGrey(1);
          setFieldValue(fields.isReallocationAllowed, value);
        }}
        component={FieldButtonGroup}
        disabled={isPlan ? true : isEdit && !isSave}
      />
      <Field
        size="sm"
        isRequired
        name={fields.isRebalanceAllowed}
        label={"Rebalancing allowed?"}
        options={yesNoOptions}
        selectedValue={values[fields.isRebalanceAllowed]}
        value={values[fields.isRebalanceAllowed]}
        onChange={(value) => {
          setTabGrey(1);
          setFieldValue(fields.isRebalanceAllowed, value);
          if (!value) {
            setFieldValue(fields.isPeriodicRebalancing, null);
            setFieldValue(fields.rebalancingFrequency, null);
            setFieldValue(fields.effectiveDate, null);
          }
        }}
        component={FieldButtonGroup}
        disabled={isPlan ? true : isEdit && !isSave}
      />
      {values[fields.isRebalanceAllowed] && (
        <>
          <Field
            size="sm"
            isRequired
            name={fields.isPeriodicRebalancing}
            label={"Periodic Rebalancing Required?"}
            options={yesNoOptions}
            selectedValue={values[fields.isPeriodicRebalancing]}
            value={values[fields.isPeriodicRebalancing]}
            onChange={(value) => {
              setTabGrey(1);
              setFieldValue(fields.isPeriodicRebalancing, value);
              !value && setFieldValue(fields.rebalancingFrequency, null);
            }}
            component={FieldButtonGroup}
            disabled={isPlan ? true : isEdit && !isSave}
          />
          {values[fields.isPeriodicRebalancing] && (
            <>
              <Field
                name={fields.rebalancingFrequency}
                isRequired
                label="Rebalancing Frequency"
                value={values[fields.rebalancingFrequency]}
                disabled={isPlan ? true : isEdit && !isSave}
                options={payrollFrequency.data}
                popupContent={
                  <SearchableList
                    label="Select an rebalancing frequency"
                    options={payrollFrequency.data}
                    onSelect={(value) => {
                      setTabGrey(1);
                      setFieldValue(fields.rebalancingFrequency, value);
                    }}
                    disabled={isPlan ? true : isEdit && !isSave}
                    isNotTypeAhead
                  />
                }
                component={FieldDropSide}
              />
              {values[fields.rebalancingFrequency] === 1 && (
                <>
                  <Field
                    label="Exclude Saturdays?"
                    name={fields.excludeSaturday}
                    isRequired
                    size="sm"
                    options={yesNoOptions}
                    selectedValue={values[fields.excludeSaturday]}
                    onChange={(value) => {
                      setFieldValue(fields.excludeSaturday, value);
                      setTabGrey(1);
                    }}
                    disabled={isEdit && !isSave}
                    component={FieldButtonGroup}
                  />
                  <Field
                    label="Exclude Sundays?"
                    name={fields.excludeSunday}
                    isRequired
                    size="sm"
                    options={yesNoOptions}
                    selectedValue={values[fields.excludeSunday]}
                    onChange={(value) => {
                      setFieldValue(fields.excludeSunday, value);
                      setTabGrey(1);
                    }}
                    disabled={isEdit && !isSave}
                    component={FieldButtonGroup}
                  />
                </>
              )}
              {values[fields.rebalancingFrequency] === 2 && (
                <Field
                  label="Start day"
                  name={fields.startDay}
                  isRequired
                  value={values[fields.startDay]}
                  options={weekdays.data}
                  popupContent={
                    <SearchableList
                      label="Select day"
                      options={weekdays.data}
                      selectedValue={values[fields.startDay]}
                      onSelect={(value) => {
                        setFieldValue(fields.startDay, value);
                        setTabGrey(1);
                      }}
                      isNotTypeAhead
                      isRadio
                    />
                  }
                  disabled={isEdit && !isSave}
                  component={FieldDropSide}
                />
              )}
            </>
          )}
          {values[fields.rebalancingFrequency] === 8 && (
            <Field
              name={fields.biWeeklyStartDate}
              isRequired
              label="Start Date"
              value={usDateFormat(values[fields.biWeeklyStartDate])}
              disabled={isEdit && !isSave}
              isDatePicker
              onClear={() => {
                setFieldValue(fields.biWeeklyStartDate, null);
                setTabGrey(1);
              }}
              popupContent={
                <DatePicker
                  onDayClick={(value) => {
                    onDaySelected(fields.biWeeklyStartDate, value);
                    setTabGrey(1);
                  }}
                  value={usDateFormat(values[fields.biWeeklyStartDate])}
                />
              }
              component={FieldDropSide}
            />
          )}
          {values[fields.rebalancingFrequency] === 3 && (
            <>
              <Field
                label="First begin day"
                name={fields.firstBeginDay}
                size="sm"
                isRequired
                value={values[fields.firstBeginDay]}
                options={days.data}
                popupContent={
                  <DayDropdown
                    fromMonthDate={new Date(2020, 0)}
                    onSelect={(value) => {
                      setFieldValue(fields.firstBeginDay, parseInt(value, 10));
                      setTabGrey(1);
                    }}
                    value={values[fields.firstBeginDay]}
                  />
                }
                disabled={isEdit && !isSave}
                component={FieldDropSide}
              />
              <Field
                label="Second begin day"
                name={fields.secondBeginDay}
                size="sm"
                isRequired
                value={values[fields.secondBeginDay]}
                options={days.data}
                popupContent={
                  <DayDropdown
                    fromMonthDate={new Date(2020, 0)}
                    onSelect={(value) => {
                      setFieldValue(fields.secondBeginDay, parseInt(value, 10));
                      setTabGrey(1);
                    }}
                    value={values[fields.secondBeginDay]}
                  />
                }
                disabled={isEdit && !isSave}
                component={FieldDropSide}
              />
            </>
          )}
          {values[fields.rebalancingFrequency] === 4 && (
            <Field
              label="Starting day of every month"
              name={fields.startDate}
              size="sm"
              isRequired
              value={values[fields.startDate]}
              options={days.data}
              popupContent={
                <DayDropdown
                  fromMonthDate={new Date(2020, 0)}
                  onSelect={(value) => {
                    setFieldValue(fields.startDate, parseInt(value, 10));
                    setTabGrey(1);
                  }}
                  value={values[fields.startDate]}
                />
              }
              disabled={isEdit && !isSave}
              component={FieldDropSide}
            />
          )}
          {values[fields.rebalancingFrequency] === 5 && (
            <Field
              label="Starting month for first quarter"
              name={fields.quarterlyStartMonth}
              isRequired
              value={values[fields.quarterlyStartMonth]}
              options={months.data}
              popupContent={
                <SearchableList
                  label="Select a month"
                  options={months.data}
                  onSelect={(value) => {
                    setFieldValue(fields.quarterlyStartMonth, value);
                    setTabGrey(1);
                  }}
                  isNotTypeAhead
                  selectedValue={values[fields.quarterlyStartMonth]}
                />
              }
              disabled={isEdit && !isSave}
              component={FieldDropSide}
            />
          )}
          {values[fields.rebalancingFrequency] === 6 && (
            <Field
              label="Starting month for the half-year"
              name={fields.halfYearlyStartMonth}
              isRequired
              value={values[fields.halfYearlyStartMonth]}
              options={months.data}
              popupContent={
                <SearchableList
                  label="Select a month"
                  options={months.data}
                  onSelect={(value) => {
                    setFieldValue(fields.halfYearlyStartMonth, value);
                    setTabGrey(1);
                  }}
                  isNotTypeAhead
                  selectedValue={values[fields.halfYearlyStartMonth]}
                />
              }
              disabled={isEdit && !isSave}
              component={FieldDropSide}
            />
          )}
          {values[fields.rebalancingFrequency] === 7 && (
            <div className="d-flex">
              <Field
                label="Starting date"
                name={fields.annuallyStartMonth}
                isRequired
                size="sm"
                value={values[fields.annuallyStartMonth]}
                options={months.data}
                placeholder="Month"
                popupContent={
                  <SearchableList
                    label="Select a month"
                    options={months.data}
                    onSelect={(value) => {
                      setFieldValue(fields.annuallyStartMonth, value);
                      setTabGrey(1);
                    }}
                    isNotTypeAhead
                    selectedValue={values[fields.annuallyStartMonth]}
                  />
                }
                disabled={isEdit && !isSave}
                component={FieldDropSide}
              />
              &nbsp; &nbsp;
              <Field
                label=" "
                size="xs"
                name={fields.annuallyStartDate}
                value={values[fields.annuallyStartDate]}
                placeholder="day"
                popupContent={
                  <DayDropdown
                    fromMonthDate={
                      new Date(2020, values[fields.annuallyStartMonth] - 1 || 0)
                    }
                    onSelect={(value) => {
                      setFieldValue(
                        fields.annuallyStartDate,
                        parseInt(value, 10)
                      );
                      setTabGrey(1);
                    }}
                    value={values[fields.annuallyStartDate]}
                  />
                }
                disabled={isEdit && !isSave}
                component={FieldDropSide}
              />
            </div>
          )}
          <Field
            size="smd"
            name={fields.effectiveDate}
            placeholder="To"
            value={usDateFormat(values[fields.effectiveDate])}
            direction="right"
            isDatePicker
            isRequired
            label="Effective Date"
            onClear={() => setFieldValue(fields.effectiveDate, "")}
            popupContent={
              <DatePicker
                onDayClick={(value) => {
                  setTabGrey(1);
                  setFieldValue(fields.effectiveDate, value);
                }}
                value={usDateFormat(values[fields.effectiveDate])}
              />
            }
            disabled={isPlan ? true : isEdit && !isSave}
            component={FieldDropSide}
          />
        </>
      )}
      <Field
        isRequired
        name={fields.marketTimingRule}
        label="Market Timing Rules"
        options={toOptionValuesFromMapper(
          OPTIONS_DATA_MAPPER.MARKET_TIMING_RULES
        )}
        selectedValue={values[fields.marketTimingRule]}
        onChange={(value) => {
          setTabGrey(1);
          setFieldValue(fields.marketTimingRule, value);
        }}
        disabled={isPlan ? true : isEdit && !isSave}
        component={FieldButtonGroup}
        size="xl"
      />
      <Field
        isRequired
        name={fields.applicableInvestments}
        label={"Applicable Investments"}
        isMultiSelect
        value={toMultiSelectValueById(
          values[fields.percentageElections],
          investmentsList.map((item, index) => ({
            label: item.investmentName,
            value: parseInt(item.investmentId),
          })),
          true,
          "investmentId"
        )}
        popupContent={
          <MultiSelectDropdown
            label="Select Applicable Investments"
            maxHeight="200px"
            height="190px"
            name={fields.percentageElections}
            isArrayOfObjects={true}
            objectKey={"investmentId"}
            isSelectAllClick={(item) => {
              setFieldValue(
                fields.percentageElections,
                item.map((i) => {
                  return (i = {
                    investmentId: i.value,
                    allocationPercentage: "",
                  });
                })
              );
            }}
            onOptionClick={(item) => {
              if (!item.checked)
                setFieldValue(fields.percentageElections, [
                  ...percentageElectionsList,
                  {
                    investmentId: item.value,
                    allocationPercentage: "",
                  },
                ]);
              else {
                setFieldValue(
                  fields.percentageElections,
                  percentageElectionsList.filter(
                    (_) => _.investmentId !== item.value
                  )
                );
              }
            }}
            onSelect={(value) => {
              setTabGrey(1);
            }}
            value={values[fields.percentageElections]}
            options={investmentsList.map((item, index) => ({
              label: item.investmentName,
              value: parseInt(item.investmentId),
            }))}
            disabled={isPlan ? true : isEdit && !isSave}
            isTypeAhead
          />
        }
        disabled={isPlan ? true : isEdit && !isSave}
        component={FieldDropSide}
      />

      <div>
        <div className="w-100">
          <div className="d-flex justify-content-between mb-4">
            <div className="">
              <div className="m-0 plan-sub-heading">% Election</div>
            </div>
          </div>
        </div>
        <Table>
          <Table.Thead>
            <Table.Tr>
              {columns.map((item, index) => {
                return (
                  <Table.Th key={index} className={item.className}>
                    {item.label}
                  </Table.Th>
                );
              })}
            </Table.Tr>
          </Table.Thead>

          <FieldArray name={fields.percentageElections}>
            {() => {
              return (
                <Table.Tbody>
                  {percentageElectionsList &&
                    percentageElectionsList.map((restriction, index) => {
                      return (
                        <Table.Tr key={index}>
                          {columns.map((item, cellIndex) => {
                            const getContent = () => {
                              const fieldName = `${fields.percentageElections}[${index}].${item.columnName}`;
                              const id = `${fields.percentageElections}[${index}]id`;

                              if (item.columnName === "allocationPercentage") {
                                return (
                                  <div className="d-flex align-items-center justify-content-between w-75">
                                    <Field
                                      size="xsm"
                                      isRequired
                                      name={fieldName}
                                      type="number"
                                      autoComplete="off"
                                      value={get(values, fieldName)}
                                      min={0}
                                      max={100}
                                      onChange={(e) => {
                                        setFieldValue(
                                          fieldName,
                                          e.target.value
                                        );
                                        setFieldValue(
                                          id,
                                          restriction.id ? restriction.id : 0
                                        );
                                      }}
                                      disabled={
                                        isPlan ? true : isEdit && !isSave
                                      }
                                      component={FieldInputNumber}
                                    />
                                  </div>
                                );
                              }
                              if (item.columnName === "investmentName") {
                                return get(
                                  investmentsList.filter(
                                    (_) =>
                                      _.investmentId ===
                                      restriction["investmentId"]
                                  ),
                                  "0.investmentName"
                                );
                              }
                              return restriction[item.columnName];
                            };

                            return (
                              <Table.Td
                                key={cellIndex}
                                className={item.className}
                              >
                                {getNullTableItem(getContent())}
                              </Table.Td>
                            );
                          })}
                        </Table.Tr>
                      );
                    })}
                </Table.Tbody>
              );
            }}
          </FieldArray>
          <Table.Thead className="mt-2">
            <Table.Tr>
              {columns.map((item, index) => {
                const getContent = () => {
                  if (index === 0) {
                    return "TOTAL";
                  }
                  if (totalAllocationPerc && index === 1) {
                    return totalAllocationPerc;
                  }
                };
                return (
                  <Table.Th key={index} className={item.className}>
                    {getContent()}
                  </Table.Th>
                );
              })}
            </Table.Tr>
          </Table.Thead>
        </Table>
        <br />
        {/* <Link
          to={getPathWithParam({
            path: MANAGE_PLAN_ROUTES.VIEW_INV_ASSOCIATED_PLANS,
            pathParam: [FLOW_TYPES.EDIT, planId, intInvestmentId],
          })}
          disabled={isPlan? true : isEdit && !isSave}
          className="blue-link"
        >
          View Associated Plans
        </Link> */}
      </div>
    </div>
  );
};

export default ModelPortfolio;
